/**
 * Replay playback — the third job of the one deterministic sim
 * (docs/BARRAGE_IMPLEMENTATION_PLAN.md "one sim, three jobs"). Given a stored
 * {@link RunRecord} ({seed, viewport, samples, mode}) it re-runs the SAME core
 * world the live game and the server's anti-cheat use, and renders it with the
 * SAME {@link PatternDirector} + {@link ensureHazardTextures} + {@link Player} the
 * live {@link import('./MainScene').MainScene} uses — so the picture on screen is
 * byte-for-byte the original run.
 *
 * Division of labour mirrors MainScene exactly: core decides, Phaser draws. The
 * {@link ReplayDirector} owns the deterministic stepping + input resolution (kept
 * in lockstep with `core/world.ts`'s `runSimulation`); this scene owns rendering,
 * the fixed-step clock, the transport (play / pause / restart), and the outcome
 * banner shown once the replay ends (death or the timeline running out).
 *
 * Coordinate space: the run was simulated at `record.viewport`. The inputs are in
 * THAT space, so the world keeps that viewport regardless of the live canvas size;
 * the scene camera zooms + centres so the whole original play area is letterboxed
 * to fit (a resize only re-fits the camera — it never re-simulates).
 *
 * Pass the record either via scene data (`scene.start('replay', { record })`) or
 * the {@link ReplayScene.create} factory + {@link bootReplay}-style host. It does
 * NOT touch MainScene/bootGame — the integrator wires it in.
 */
import Phaser from 'phaser';
import type { AttackFamily, Hazard, RunRecord, WorldState } from '../core';
import { PLAYER, FixedStepClock, fixedDeltaSeconds } from '../core';
import { PatternDirector } from './PatternDirector';
import { Background } from './Background';
import { ensureHazardTextures } from './textures';
import { Player } from './Player';
import { getSkin, DEFAULT_SKIN_ID } from './skins';
import { ReplayDirector } from './ReplayDirector';
import { fitCameraToField, alignHud } from './viewport';

/** The Phaser scene key the replay registers under. */
export const REPLAY_SCENE_KEY = 'replay';

/**
 * Scene-data contract for {@link ReplayScene}. Accepted via
 * `scene.start(REPLAY_SCENE_KEY, data)` so the integrator can launch a replay
 * without constructing the scene by hand. The constructor path
 * ({@link ReplayScene.create}) takes the same fields directly.
 */
export interface ReplaySceneData {
  /** The stored run to play back. */
  record: RunRecord;
  /** Ball skin id to render the player with (defaults to {@link DEFAULT_SKIN_ID}). */
  skinId?: string;
  /** Start paused instead of auto-playing (default: play on load). */
  startPaused?: boolean;
}

/** Human-readable label per attack family for the outcome banner. */
const FAMILY_LABEL: Record<AttackFamily, string> = {
  walls: 'a Wall',
  lasers: 'a Laser Wall',
  turret: 'the Turret',
  caro: 'the Carousel',
  missiles: 'a Missile',
  bombs: 'a Bomb',
};

const FONT = 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

/**
 * Plays back a {@link RunRecord} deterministically. Stateless re: the run's logic
 * (that all lives in core via {@link ReplayDirector}); this class only renders and
 * runs the transport. Auto-cleans its scene-scoped listeners; the one external
 * listener it registers (`scale.on('resize')`) is removed on shutdown, matching
 * the MainScene leak-avoidance discipline (CLAUDE.md "Gotchas").
 */
export class ReplayScene extends Phaser.Scene {
  /** Optional record supplied via the constructor factory; scene-data overrides it. */
  private ctorData: ReplaySceneData | null;

  private replay!: ReplayDirector;
  private director!: PatternDirector;
  private background!: Background;
  private player!: Player;
  private skinId = DEFAULT_SKIN_ID;

  private readonly clock = new FixedStepClock();

  /** Transport state: true while advancing, false when paused or finished. */
  private playing = false;
  /** Latched once the outcome banner has been shown for this play-through. */
  private ended = false;
  /** Guard so the cleanup runs exactly once (SHUTDOWN or DESTROY). */
  private cleanedUp = false;

  // In-canvas HUD (parity with MainScene; React never sees per-frame state). Lives in
  // a counter-transformed container so it stays upright over the zoomed/rotated camera.
  private hud!: Phaser.GameObjects.Container;
  private scoreText!: Phaser.GameObjects.Text;
  private progressBg!: Phaser.GameObjects.Rectangle;
  private progressBar!: Phaser.GameObjects.Rectangle;
  private bannerBg!: Phaser.GameObjects.Rectangle;
  private bannerText!: Phaser.GameObjects.Text;

  /**
   * @param data optional record + options. Prefer launching via
   *   `scene.start(REPLAY_SCENE_KEY, data)`; this ctor path exists for hosts that
   *   add the scene instance directly (e.g. a dedicated replay boot).
   */
  constructor(data?: ReplaySceneData) {
    super({ key: REPLAY_SCENE_KEY });
    this.ctorData = data ?? null;
  }

  /**
   * Convenience factory for a one-off replay scene instance (mirrors how
   * StartFlowScene is constructed with its bus). Equivalent to `new ReplayScene`
   * with the data baked in.
   */
  static create(record: RunRecord, opts?: Omit<ReplaySceneData, 'record'>): ReplayScene {
    return new ReplayScene({ record, ...opts });
  }

  /** Phaser passes scene-data here (from `scene.start(key, data)`). */
  init(data?: Partial<ReplaySceneData>): void {
    // Scene-data (if any) wins over a constructor-supplied record; this lets the
    // same registered scene be re-launched with different runs via scene.start.
    const merged: ReplaySceneData | null =
      data && data.record ? (data as ReplaySceneData) : this.ctorData;
    if (!merged) {
      throw new Error('ReplayScene requires a RunRecord (via scene data or the constructor).');
    }
    this.ctorData = merged;
    this.skinId = merged.skinId ?? DEFAULT_SKIN_ID;
  }

  create(): void {
    const data = this.ctorData!;
    const record = data.record;

    ensureHazardTextures(this);

    // Arena backdrop, sized to the RECORD's play area so it fills the (letterboxed)
    // world exactly as the live game did — not a black field.
    this.background = new Background(this);
    this.background.resize(record.viewport.width, record.viewport.height);

    // Deterministic playback + hazard renderer (the exact pair MainScene uses).
    this.replay = new ReplayDirector(record);
    this.director = new PatternDirector(this);

    // Player ball rendered at the world's starting position (record's viewport).
    const start = this.replay.world;
    this.player = new Player(this, start.playerX, start.playerY, getSkin(this.skinId));

    // Fit + orient the original play area into the live canvas (rotates 90° on a
    // portrait mobile, like the live game). Build the HUD, then align it upright.
    this.buildHud();
    fitCameraToField(this, record.viewport.width, record.viewport.height);
    alignHud(this, this.hud);

    // Re-fit on resize (the one external listener; removed on cleanup). Clean up on
    // SHUTDOWN AND DESTROY — the replay host unmounts via game.destroy(), which fires
    // only DESTROY (never SHUTDOWN).
    this.cleanedUp = false;
    this.scale.on('resize', this.onResize, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    this.events.once(Phaser.Scenes.Events.DESTROY, this.onShutdown, this);

    // Initial paint, then start the transport (unless asked to begin paused).
    this.clock.reset();
    this.director.sync(this.replay.world);
    this.renderPlayer();
    this.refreshHud();

    this.playing = !data.startPaused;
  }

  /* --------------------------------------------------------------------- *
   * Transport — play / pause / restart. Public so the integrator can wire
   * them to UI buttons (e.g. via a small command bus) without poking internals.
   * --------------------------------------------------------------------- */

  /** Resume advancing the replay (no-op once finished). */
  play(): void {
    if (this.ended) return;
    this.clock.reset(); // drop any time accumulated while paused
    this.playing = true;
  }

  /** Halt advancing; the current frame stays on screen. */
  pause(): void {
    this.playing = false;
  }

  /** Toggle play/pause. Returns the new playing state. */
  togglePlay(): boolean {
    if (this.playing) this.pause();
    else this.play();
    return this.playing;
  }

  /** True while the replay is actively advancing. */
  get isPlaying(): boolean {
    return this.playing;
  }

  /** True once the replay has reached its end (death or timeline exhausted). */
  get isFinished(): boolean {
    return this.ended;
  }

  /**
   * Rewind to the very start and play again. Rebuilds the world from the same
   * record (the seeded RNG re-derives the identical hazard stream), clears the
   * sprites, and resumes — so it works mid-playback and after the outcome banner.
   */
  restart(): void {
    this.replay.restart();
    this.director.reset();
    this.player.image.clearTint(); // undo the game-over tint from a prior finish()

    this.ended = false;
    this.hideBanner();
    this.clock.reset();

    this.director.sync(this.replay.world);
    this.renderPlayer();
    this.refreshHud();

    this.playing = true;
  }

  override update(_time: number, delta: number): void {
    // Drift/twinkle the backdrop even while paused or after the run ends.
    this.background.update(Math.min(delta, 100) / 1000);
    if (!this.playing || this.ended) return;

    const steps = this.clock.step(delta);
    if (steps === 0) return;

    const finished = this.replay.advance(steps);

    // Reconcile sprites + ball to wherever the world ended up this frame.
    this.director.sync(this.replay.world);
    this.renderPlayer();
    this.refreshHud();

    if (finished) this.finish();
  }

  /* --------------------------------------------------------------------- *
   * Rendering helpers.
   * --------------------------------------------------------------------- */

  /** Mirror the core player position onto the ball + advance its googly face. */
  private renderPlayer(): void {
    const world = this.replay.world;
    this.player.setPosition(world.playerX, world.playerY);
    const danger = this.findNearestDanger(world, PLAYER.eyeLookRange);
    this.player.update(fixedDeltaSeconds, danger, PLAYER.eyeLookRange);
  }

  /**
   * Nearest live hazard centre within `lookRange` for the googly-eye lock-on —
   * purely cosmetic, identical to MainScene's helper (collision lives in core).
   */
  private findNearestDanger(world: WorldState, lookRange: number): { x: number; y: number } | null {
    const px = world.playerX;
    const py = world.playerY;
    let best: { x: number; y: number } | null = null;
    let bestDist = lookRange;
    for (const h of world.hazards) {
      const p = hazardCenter(h);
      const dist = Math.hypot(p.x - px, p.y - py);
      if (dist < bestDist) {
        bestDist = dist;
        best = p;
      }
    }
    return best;
  }

  /* --------------------------------------------------------------------- *
   * In-canvas HUD + outcome banner — built into a single container that
   * {@link alignHud} keeps UPRIGHT and correctly sized over the zoomed/rotated
   * world camera (it counters both zoom AND rotation, so the HUD ignores the
   * letterbox/orientation transform). Children are authored in screen px.
   * --------------------------------------------------------------------- */

  private buildHud(): void {
    // All HUD elements live in one container (screen px) that {@link alignHud} keeps
    // upright + correctly sized over the zoomed/rotated world camera.
    this.hud = this.add.container(0, 0).setDepth(25);
    const { width, height } = this.scale.gameSize;

    this.scoreText = this.add.text(16, 12, 'REPLAY · Score 0', {
      fontFamily: FONT,
      fontSize: '20px',
      color: '#e8ecff',
    });

    const barW = Math.min(360, width - 32);
    const barX = 16;
    const barY = 48;
    this.progressBg = this.add.rectangle(barX, barY, barW, 6, 0x222740).setOrigin(0, 0.5);
    this.progressBar = this.add.rectangle(barX, barY, 0, 6, 0x2ec4b6).setOrigin(0, 0.5);

    // Outcome banner (hidden until the replay ends), centred on the canvas.
    const cx = width / 2;
    const cy = height / 2;
    this.bannerBg = this.add
      .rectangle(cx, cy, Math.min(420, width - 48), 96, 0x0b0f1a, 0.82)
      .setOrigin(0.5)
      .setStrokeStyle(2, 0x2ec4b6)
      .setVisible(false);
    this.bannerText = this.add
      .text(cx, cy, '', {
        fontFamily: FONT,
        fontSize: '22px',
        color: '#e8ecff',
        align: 'center',
        wordWrap: { width: Math.min(380, width - 64) },
      })
      .setOrigin(0.5)
      .setVisible(false);

    this.hud.add([this.scoreText, this.progressBg, this.progressBar, this.bannerBg, this.bannerText]);
  }

  private refreshHud(): void {
    this.scoreText.setText(`REPLAY · Score ${this.replay.currentScore}`);
    this.progressBar.width = this.progressBg.width * this.replay.progress;
  }

  /** Stop the transport, freeze the ball, and show the outcome banner once. */
  private finish(): void {
    if (this.ended) return;
    this.ended = true;
    this.playing = false;
    this.progressBar.width = this.progressBg.width; // snap to full

    const outcome = this.replay.outcome();
    this.player.setDead();

    const headline =
      outcome.reason === 'death'
        ? outcome.deathFamily
          ? `Eliminated by ${FAMILY_LABEL[outcome.deathFamily]}`
          : 'Run ended'
        : 'Run complete';
    this.bannerText.setText(`${headline}\nScore ${outcome.score}`);
    this.showBanner();
  }

  private showBanner(): void {
    this.bannerBg.setVisible(true);
    this.bannerText.setVisible(true);
  }

  private hideBanner(): void {
    this.bannerBg.setVisible(false);
    this.bannerText.setVisible(false);
  }

  private onResize = (gameSize: Phaser.Structs.Size): void => {
    // Re-fit + re-orient the camera (the record's field is unchanged), realign the HUD.
    if (this.replay) {
      const v = this.replay.record.viewport;
      fitCameraToField(this, v.width, v.height);
    }
    if (this.hud) alignHud(this, this.hud);

    // Reflow the HUD/banner to the new size (children are in screen px).
    const { width, height } = gameSize;
    if (this.progressBg) this.progressBg.width = Math.min(360, width - 32);
    if (this.bannerBg) this.bannerBg.setPosition(width / 2, height / 2);
    if (this.bannerText) this.bannerText.setPosition(width / 2, height / 2);
    this.refreshHud();
  };

  private onShutdown = (): void => {
    if (this.cleanedUp) return; // idempotent (may fire on both SHUTDOWN and DESTROY)
    this.cleanedUp = true;
    this.scale.off('resize', this.onResize, this);
    this.director?.destroy();
    this.background?.destroy();
  };
}

/** A representative centre point for a hazard (used only for the eye lock-on). */
function hazardCenter(h: Hazard): { x: number; y: number } {
  switch (h.kind) {
    case 'rect':
      return { x: h.x + h.w / 2, y: h.y + h.h / 2 };
    case 'segment':
      return { x: (h.x1 + h.x2) / 2, y: (h.y1 + h.y2) / 2 };
    case 'rotatingSegment':
      return { x: h.cx, y: h.cy };
    case 'circleMover':
      return { x: h.x, y: h.y };
  }
}
